import { isSafari } from '@/lib/platform';
import { logNoiseSuppressionToggled } from '@/services/logger/call-telemetry';
import { logger } from '@/services/logger/logger';
import { useVoiceCallState } from '@packages/client-sdk-react/src/voice/voice-call-container';
import { useVonageClient } from '@packages/client-sdk-react/src/vonage-client-provider';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

interface AudioDeviceState {
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  selectedMicrophone: string;
  selectedSpeaker: string;
  noiseSuppression: boolean;
}

interface AudioDevicesContextType extends AudioDeviceState {
  setSelectedMicrophone: (deviceId: string) => Promise<MediaStreamTrack | null>;
  setSelectedSpeaker: (deviceId: string) => Promise<void>;
  setNoiseSuppression: (enabled: boolean) => Promise<void>;
  isApplyingNoiseSuppression: boolean;
  refreshDevices: () => Promise<void>;
  currentMicrophoneStream: MediaStream | null;
}

const AudioDevicesContext = createContext<AudioDevicesContextType | null>(null);

const NOISE_SUPPRESSION_STORAGE_KEY = 'noiseSuppression';

const getAudioConstraint = (deviceId: string, noiseSuppression: boolean): MediaTrackConstraints => {
  const constraint: MediaTrackConstraints = isSafari()
    ? { deviceId: { ideal: deviceId } }
    : { deviceId: { exact: deviceId } };

  constraint.noiseSuppression = noiseSuppression;
  return constraint;
};

// Resolve 'default' deviceId to the actual device ID by matching the label
const resolveDefaultDeviceId = async (
  deviceId: string,
  deviceKind: 'audioinput' | 'audiooutput',
): Promise<string> => {
  if (deviceId !== 'default') {
    return deviceId;
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  const devicesOfKind = devices.filter((d) => d.kind === deviceKind);

  // Find the "default" device entry
  const defaultDevice = devicesOfKind.find((d) => d.deviceId === 'default');

  if (!defaultDevice?.label) {
    // Can't resolve without label, return first non-default device
    const firstDevice = devicesOfKind.find((d) => d.deviceId !== 'default');
    return firstDevice?.deviceId || deviceId;
  }

  // The default device label is typically "Default - Actual Device Name"
  const match = defaultDevice.label.match(/^Default\s*-\s*(.+)$/i);
  if (!match?.[1]) {
    return deviceId;
  }

  const actualDeviceName = match[1].trim();

  // Find the device with that name (not the 'default' one)
  const actualDevice = devicesOfKind.find(
    (d) => d.deviceId !== 'default' && d.label === actualDeviceName,
  );

  return actualDevice?.deviceId || deviceId;
};

export const AudioDevicesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [audioDevices, setAudioDevices] = useState<{
    microphones: MediaDeviceInfo[];
    speakers: MediaDeviceInfo[];
  }>({ microphones: [], speakers: [] });

  const [selectedMicrophone, setSelectedMicrophoneState] = useState<string>('');
  const [selectedSpeaker, setSelectedSpeakerState] = useState<string>('');
  const [noiseSuppression, setNoiseSuppressionState] = useState<boolean>(() => {
    const stored = localStorage.getItem(NOISE_SUPPRESSION_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });
  const [isApplyingNoiseSuppression, setIsApplyingNoiseSuppression] = useState<boolean>(false);
  const [currentMicrophoneStream, setCurrentMicrophoneStream] = useState<MediaStream | null>(null);

  const [call] = useVoiceCallState();
  const vonageClient = useVonageClient();

  // Keep a live ref to the current callId so async code in setSelectedMicrophone
  // can re-check after awaits (closure captures are stale after await points)
  const callIdRef = useRef<string | undefined>(call?.callId);
  useEffect(() => {
    callIdRef.current = call?.callId;
  }, [call?.callId]);

  // Ref so setSelectedMicrophone reads the latest noiseSuppression value without
  // being re-created (which would retrigger the auto-apply effect on every toggle)
  const noiseSuppressionRef = useRef<boolean>(noiseSuppression);
  useEffect(() => {
    noiseSuppressionRef.current = noiseSuppression;
  }, [noiseSuppression]);

  // Serialization lock for setNoiseSuppression. Rapid toggles are rejected
  // while a previous toggle's mic re-acquisition is still in flight; without
  // this, concurrent calls race on peer-connection track replacement and
  // each captures a stale `previous` value for rollback.
  const noiseSuppressionInFlightRef = useRef<boolean>(false);

  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const microphones = devices.filter((device) => device.kind === 'audioinput');
      const speakers = devices.filter((device) => device.kind === 'audiooutput');

      setAudioDevices({ microphones, speakers });

      // Set default selections if none are set and no saved preferences exist
      const savedMicrophone = localStorage.getItem('selectedMicrophone');
      const savedSpeaker = localStorage.getItem('selectedSpeaker');

      setSelectedMicrophoneState((currentMic) => {
        if (!currentMic) {
          if (savedMicrophone && microphones.find((m) => m.deviceId === savedMicrophone)) {
            return savedMicrophone;
          }
        }

        if (currentMic) {
          if (microphones.find((m) => m.deviceId === currentMic)) {
            return currentMic;
          }
        }

        return 'default';
      });

      setSelectedSpeakerState((currentSpeaker) => {
        if (!currentSpeaker) {
          if (savedSpeaker && speakers.find((m) => m.deviceId === savedSpeaker)) {
            return savedSpeaker;
          }
        }

        if (currentSpeaker) {
          if (speakers.find((m) => m.deviceId === currentSpeaker)) {
            return currentSpeaker;
          }
        }

        return 'default';
      });
    } catch (error) {
      logger.error('Error getting audio devices:', { error });
    }
  }, []);

  const setSelectedMicrophone = useCallback(
    async (deviceId: string): Promise<MediaStreamTrack | null> => {
      if (!deviceId || !call?.callId) {
        return null;
      }

      try {
        // Resolve 'default' to actual device ID for reliable switching
        const resolvedDeviceId = await resolveDefaultDeviceId(deviceId, 'audioinput');

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: getAudioConstraint(resolvedDeviceId, noiseSuppressionRef.current),
          video: false,
        });

        // Re-check callId after async getUserMedia — use ref to read latest value,
        // since closure-captured `call` is stale across the await
        const liveCallId = callIdRef.current;
        if (!liveCallId) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return null;
        }

        const peerConn = vonageClient.getPeerConnection(liveCallId);

        if (!peerConn) {
          // Stop the newly created stream since we can't use it
          for (const track of stream.getTracks()) {
            track.stop();
          }
          logger.warn('Cannot get peer connection from Vonage Client');
          return null;
        }

        const currentSenders = peerConn.getSenders();

        const currentAudioSender = currentSenders.find((s) => s.track?.kind === 'audio');

        // Capture the old track before replacing — the SDK's original getUserMedia track
        // is not tracked by currentMicrophoneStream and would leak otherwise
        const oldSenderTrack = currentAudioSender?.track;

        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          await currentAudioSender?.replaceTrack(audioTrack);
        }

        // Stop the old sender track after replacement is confirmed
        if (oldSenderTrack && oldSenderTrack !== audioTrack) {
          oldSenderTrack.stop();
        }

        // Stop tracks on the previous stream only after replaceTrack resolves
        setCurrentMicrophoneStream((prev) => {
          if (prev) {
            for (const track of prev.getTracks()) {
              track.stop();
            }
          }
          return stream;
        });
        localStorage.setItem('selectedMicrophone', deviceId);
        setSelectedMicrophoneState(deviceId);
        return audioTrack ?? null;
      } catch (error) {
        logger.error('Error setting microphone:', { error });
        throw error;
      }
    },
    [call, vonageClient],
  );

  const setSelectedSpeaker = useCallback(
    async (deviceId: string) => {
      if (!deviceId) {
        return;
      }
      try {
        localStorage.setItem('selectedSpeaker', deviceId);

        // Resolve 'default' to actual device ID for reliable switching
        const resolvedDeviceId = await resolveDefaultDeviceId(deviceId, 'audiooutput');

        const vonageAudioOutputElement = vonageClient.getAudioOutputElement();

        try {
          await vonageAudioOutputElement?.setSinkId(resolvedDeviceId);
          setSelectedSpeakerState(deviceId);
        } catch (error) {
          logger.error('Error setting sink ID for audio element:', { error });
        }
      } catch (error) {
        logger.error('Error setting speaker:', { error });
        throw error;
      }
    },
    [vonageClient],
  );

  const setNoiseSuppression = useCallback(
    async (enabled: boolean) => {
      // Drop the call if a previous toggle is still in flight — the UI also
      // disables the Switch, so this is belt-and-braces against programmatic
      // callers or rapid double-events
      if (noiseSuppressionInFlightRef.current) {
        return;
      }

      const previous = noiseSuppressionRef.current;
      if (previous === enabled) {
        return;
      }

      noiseSuppressionInFlightRef.current = true;
      setIsApplyingNoiseSuppression(true);

      // Apply optimistically so getAudioConstraint picks it up during re-acquisition
      localStorage.setItem(NOISE_SUPPRESSION_STORAGE_KEY, String(enabled));
      noiseSuppressionRef.current = enabled;
      setNoiseSuppressionState(enabled);

      // No active call — the preference is saved and will apply on the next call
      if (!callIdRef.current || !selectedMicrophone) {
        logNoiseSuppressionToggled({
          callId: callIdRef.current,
          requested: enabled,
          applied: null,
          applyScope: 'preference-only',
        });
        noiseSuppressionInFlightRef.current = false;
        setIsApplyingNoiseSuppression(false);
        return;
      }

      try {
        const audioTrack = await setSelectedMicrophone(selectedMicrophone);
        const settings = audioTrack?.getSettings();
        const applied =
          typeof settings?.noiseSuppression === 'boolean' ? settings.noiseSuppression : null;

        logNoiseSuppressionToggled({
          callId: callIdRef.current,
          requested: enabled,
          applied,
          applyScope: 'in-call',
          microphoneLabel: audioTrack?.label,
        });
      } catch (error) {
        // Roll back so UI and live audio track stay in sync, then re-throw so the
        // caller can skip success side-effects (e.g. analytics)
        localStorage.setItem(NOISE_SUPPRESSION_STORAGE_KEY, String(previous));
        noiseSuppressionRef.current = previous;
        setNoiseSuppressionState(previous);

        logNoiseSuppressionToggled({
          callId: callIdRef.current,
          requested: enabled,
          applied: null,
          applyScope: 'in-call-failed',
          error: error instanceof Error ? error.message : String(error),
        });
        logger.error('Error applying noise suppression change:', { error });
        throw error;
      } finally {
        noiseSuppressionInFlightRef.current = false;
        setIsApplyingNoiseSuppression(false);
      }
    },
    [selectedMicrophone, setSelectedMicrophone],
  );

  useEffect(() => {
    refreshDevices();

    const handleDeviceChange = () => {
      refreshDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [refreshDevices]);

  useEffect(() => {
    return () => {
      // Stop the current microphone stream on unmount
      setCurrentMicrophoneStream((prev) => {
        if (prev) {
          for (const track of prev.getTracks()) {
            track.stop();
          }
        }
        return null;
      });
    };
  }, []);

  // Stop microphone stream when call ends to release the mic (clears macOS mic indicator)
  useEffect(() => {
    if (call?.callState === 'idle') {
      setCurrentMicrophoneStream((prev) => {
        if (prev) {
          for (const track of prev.getTracks()) {
            track.stop();
          }
        }
        return null;
      });
    }
  }, [call?.callState]);

  // Track whether we've already applied the saved mic for the current call
  const appliedMicForCallRef = useRef<string | null>(null);

  // Auto-apply saved microphone when call becomes active, but only if the
  // saved device differs from the one the SDK already negotiated.
  // The Vonage SDK acquires the system-default mic during serverCall(), so
  // re-applying 'default' is a no-op that used to cause an audio gap on every call.
  useEffect(() => {
    if (call?.callState !== 'in-progress' || !call.callId) {
      // Reset when callId clears — new callId on the next call will not match the stale ref
      if (!call?.callId) {
        appliedMicForCallRef.current = null;
      }
      return;
    }

    // Already applied for this call
    if (appliedMicForCallRef.current === call.callId) {
      return;
    }

    // Capture the narrowed callId for use inside setTimeout closures
    const currentCallId = call.callId;

    // For 'default' or empty, always do the replacement.
    // On Windows/Chrome, the mic pre-check in use-server-call.ts (getUserMedia → stop)
    // can leave the SDK's subsequent track in a broken state — technically 'live' but
    // not producing samples. Label matching can't detect this since the broken track
    // still carries the correct label. Accepting the small audio gap is safer than
    // risking silent outbound audio.
    if (!selectedMicrophone || selectedMicrophone === 'default') {
      const timer = setTimeout(() => {
        // Claim synchronously to prevent duplicate timers from the effect re-running
        // while setSelectedMicrophone is in-flight (e.g., call object changes due to
        // mute/contact updates recreate setSelectedMicrophone via useCallback deps).
        appliedMicForCallRef.current = currentCallId;
        setSelectedMicrophone(selectedMicrophone || 'default').catch((error) => {
          // Release on failure so the effect can retry — but only if this call still
          // owns the ref (avoid clobbering a newer call's claim on late async failure).
          if (appliedMicForCallRef.current === currentCallId) {
            appliedMicForCallRef.current = null;
          }
          logger.error('Auto-apply default mic failed:', { error, callId: currentCallId });
        });
      }, 500);
      return () => clearTimeout(timer);
    }

    // For specific saved devices, check if the SDK already picked the right one.
    // This avoids an unnecessary replacement when the user explicitly selected a
    // device that the SDK also happens to negotiate (common when the saved device
    // is the OS default).
    const pc = vonageClient.getPeerConnection(currentCallId);
    const currentSender = pc?.getSenders().find((s) => s.track?.kind === 'audio');
    const currentTrackLabel = currentSender?.track?.label ?? '';

    const savedDevice = audioDevices.microphones.find((m) => m.deviceId === selectedMicrophone);
    if (savedDevice && currentTrackLabel === savedDevice.label) {
      appliedMicForCallRef.current = currentCallId;
      return;
    }

    // The saved device is different from what the SDK picked — apply it.
    const timer = setTimeout(() => {
      appliedMicForCallRef.current = currentCallId;
      setSelectedMicrophone(selectedMicrophone).catch((error) => {
        if (appliedMicForCallRef.current === currentCallId) {
          appliedMicForCallRef.current = null;
        }
        logger.error('Auto-apply saved mic failed:', { error, callId: currentCallId });
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [
    call?.callState,
    call?.callId,
    selectedMicrophone,
    setSelectedMicrophone,
    vonageClient,
    audioDevices.microphones,
  ]);

  const value: AudioDevicesContextType = {
    microphones: audioDevices.microphones,
    speakers: audioDevices.speakers,
    selectedMicrophone,
    selectedSpeaker,
    noiseSuppression,
    isApplyingNoiseSuppression,
    setSelectedMicrophone,
    setSelectedSpeaker,
    setNoiseSuppression,
    refreshDevices,
    currentMicrophoneStream,
  };

  return <AudioDevicesContext.Provider value={value}>{children}</AudioDevicesContext.Provider>;
};

export const useAudioDevices = (): AudioDevicesContextType => {
  const context = useContext(AudioDevicesContext);
  if (!context) {
    throw new Error('useAudioDevices must be used within AudioDevicesProvider');
  }
  return context;
};
